import React, { memo, useCallback, useMemo, useRef, Fragment } from 'react';
import { generateUUID } from '@Sage/ssm-utils';
import { Advice, Icon, useTheme } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import useOnboarding from '../../hooks/useOnboarding';
import { Modal, Table, EditForm, AddForm } from '../';
import { types, TABLES, ORDER_STATUS } from '../../../constants';

const OrdersStates = ({ getList, setList, handleError, handleSetCount }) => {
    const modalInputRef = useRef(null);
    const { t } = useWeb5Context();
    const theme = useTheme();
    const {
        state,
        dispatch,
        handleDragEnter,
        handleDragEnd,
        handleDelete,
        handleEdit,
        handleEditFormChange: onEditFormChange,
        handleEditFormBlur,
        handleAddFormChange: onAddFormChange,
        handleAddFormBlur,
        handleUpdate,
        formHasErrors,
        isRequired,
        validateAddForm,
    } = useOnboarding({
        getData: () => getList(TABLES.SALES_ORDER_STATUS),
        getDataCallback: (data) => data.sort((a, b) => a.order - b.order),
        handleError,
        handleSetCount: (value) => handleSetCount(TABLES.SALES_ORDER_STATUS, value),
        setData: (data) => setList(TABLES.SALES_ORDER_STATUS, data),
        parseData: (data) => data.map((item, index) => ({ ...item, order: index })),
    });

    const orderStatusCount = useMemo(
        () =>
            state.data.reduce(
                (totals, item) => ({
                    [ORDER_STATUS.WON]: item.won
                        ? [...totals[ORDER_STATUS.WON], item.id]
                        : totals[ORDER_STATUS.WON],
                    [ORDER_STATUS.LOST]: item.lost
                        ? [...totals[ORDER_STATUS.LOST], item.id]
                        : totals[ORDER_STATUS.LOST],
                    [ORDER_STATUS.OPEN]:
                        !item.won && !item.lost
                            ? [...totals[ORDER_STATUS.OPEN], item.id]
                            : totals[ORDER_STATUS.OPEN],
                }),
                {
                    [ORDER_STATUS.WON]: [],
                    [ORDER_STATUS.LOST]: [],
                    [ORDER_STATUS.OPEN]: [],
                },
            ),
        [state.data],
    );

    const openStatusOption = useMemo(
        () => ({
            label: t('cfm_label_satate_open'),
            value: ORDER_STATUS.OPEN,
            count: orderStatusCount[ORDER_STATUS.OPEN].length,
            adviceCount: t('cfm_label_mandatory_1_state_open'),
        }),
        [t, orderStatusCount],
    );

    const wonStatusOption = useMemo(
        () => ({
            label: t('cfm_label_satate_closed_won'),
            value: ORDER_STATUS.WON,
            count: orderStatusCount[ORDER_STATUS.WON].length,
            adviceCount: t('cfm_label_mandatory_1_state_won'),
        }),
        [t, orderStatusCount],
    );

    const lostStatusOption = useMemo(
        () => ({
            label: t('cfm_label_satate_closed_lost'),
            value: ORDER_STATUS.LOST,
            count: orderStatusCount[ORDER_STATUS.LOST].length,
            adviceCount: t('cfm_label_mandatory_1_state_lost'),
        }),
        [t, orderStatusCount],
    );

    const statusOptions = useMemo(
        () => [openStatusOption, wonStatusOption, lostStatusOption],
        [lostStatusOption, openStatusOption, wonStatusOption],
    );

    const getStatus = useCallback(
        (won, lost) => {
            switch (true) {
                case won:
                    return wonStatusOption;
                case lost:
                    return lostStatusOption;
                default:
                    return openStatusOption;
            }
        },
        [lostStatusOption, openStatusOption, wonStatusOption],
    );

    const setStatus = useCallback((value) => {
        switch (value) {
            case ORDER_STATUS.OPEN:
                return {
                    won: false,
                    lost: false,
                };
            case ORDER_STATUS.WON:
                return {
                    won: true,
                    lost: false,
                };
            case ORDER_STATUS.LOST:
                return {
                    won: false,
                    lost: true,
                };
            default:
                break;
        }
    }, []);

    const getHints = useMemo(() => {
        const modalType = state.modal.type;
        const modal = state.modal[modalType];
        const { id, status: { value, adviceCount, count } = {} } = modal?.values || {};
        return {
            adviceCount:
                count === 1 && orderStatusCount[value].includes(id) ? adviceCount : undefined,
        };
    }, [orderStatusCount, state.modal]);

    const formSchema = useMemo(() => {
        const { adviceCount } = getHints;
        const isReadOnly = !!adviceCount;

        return [
            {
                label: t('label_order_satate'),
                placeholder: t('placeholder_text_field'),
                labelMode: 'vertical',
                name: 'name',
                type: 'text',
                isRequired: true,
                ref: (ref) => (modalInputRef.current = ref),
            },
            {
                label: t('cfm_label_state_open_or_close'),
                placeholder: t('label_selectone'),
                labelMode: 'vertical',
                name: 'status',
                type: 'select',
                isReadOnly,
                isClearable: false,
                isRequired: true,
                attrs: {
                    options: statusOptions,
                    overrides: {
                        controlFocused: {
                            style: {
                                border: `1px solid ${theme.colors.actionMajor[500]}`,
                            },
                        },
                    },
                },
            },
        ];
    }, [getHints, statusOptions, t, theme.colors.actionMajor]);

    const validateField = useCallback(
        (field, value) => {
            let error = null;

            switch (field) {
                case 'name':
                    error = isRequired(value);
                    break;
                case 'status':
                    error = isRequired(value);
                    break;
                default:
                    break;
            }

            return error;
        },
        [isRequired],
    );

    const handleAddFormChange = useCallback(
        (payload) => onAddFormChange(payload, validateField, formSchema),
        [onAddFormChange, validateField, formSchema],
    );

    const handleAddFormSave = useCallback(() => {
        const form = state.modal[types.MODAL_ADD];
        const addedStates = form.values;
        const currentStates = state.data;

        if (!validateAddForm(addedStates, validateField, formSchema)) return;

        let newTypes = addedStates.reduce(
            (arr, item) => [
                ...arr,
                {
                    id: generateUUID(),
                    temp: true,
                    name: item.name,
                    ...setStatus(item.status.value),
                },
            ],
            [...currentStates],
        );

        handleUpdate(newTypes);
        dispatch({ type: types.CLOSE_MODAL });
    }, [
        state.modal,
        state.data,
        validateAddForm,
        validateField,
        formSchema,
        handleUpdate,
        dispatch,
        setStatus,
    ]);

    const handleEditFormSave = useCallback(() => {
        const { id, name, status } = state.modal[types.MODAL_EDIT].values;
        const currentStates = state.data;

        const newStates = currentStates.reduce((arr, state) => {
            let newState;

            if (state.id === id) {
                newState = {
                    ...state,
                    name,
                    ...setStatus(status.value),
                };
            } else {
                newState = state;
            }

            return [...arr, newState];
        }, []);

        handleUpdate(newStates);
        dispatch({ type: types.CLOSE_MODAL });
    }, [state.modal, state.data, handleUpdate, dispatch, setStatus]);

    const handleEditFormChange = useCallback(
        (payload) => onEditFormChange(payload, validateField),
        [onEditFormChange, validateField],
    );

    const getInfoForTag = useCallback(
        (data) => {
            if (!data) return null;
            const { lost, won } = data;
            let type = undefined;
            let literal = undefined;

            switch (true) {
                case won:
                    type = 'success';
                    literal = 'cfm_label_satate_closed_won';
                    break;
                case lost:
                    type = 'error';
                    literal = 'cfm_label_satate_closed_lost';
                    break;
                case !won && !lost:
                    literal = 'cfm_label_satate_open';
                    break;
                default:
                    break;
            }

            return {
                type,
                value: `${t(literal)}`,
            };
        },
        [t],
    );

    const tableProps = useMemo(() => {
        return {
            title: t('label_orders_states_title'),
            description: t('label_orders_states_title_desc'),
            actionText: t('action_add_states'),
            actionOnClick: () => dispatch({ type: types.OPEN_MODAL, value: types.MODAL_ADD }),
            columnDefs: [
                {
                    colId: 'name',
                    headerName: t('label_order_satate'),
                    field: 'name',
                    suppressSizeToFit: false,
                    resizable: false,
                    rowDrag: true,
                },
                {
                    colId: 'status',
                    headerName: t('cfm_label_state_open_or_close'),
                    field: 'status',
                    suppressSizeToFit: false,
                    resizable: false,
                    cellRenderer: 'tagCell',
                    cellRendererParams: {
                        align: 'right',
                        innerMethods: {
                            getInfoForTag,
                        },
                    },
                },
            ],
            rowData: state.data,
            actions: [
                (row) => ({
                    icon: (
                        <Icon
                            name="edit"
                            onClick={() =>
                                handleEdit({
                                    id: row.id,
                                    name: row.name,
                                    status: getStatus(row.won, row.lost),
                                })
                            }
                        />
                    ),
                    tooltip: { content: t('action_edit') },
                }),
                (row) => {
                    return (!row.readOnly &&
                        !row.won &&
                        !row.lost &&
                        orderStatusCount[ORDER_STATUS.OPEN].length > 1) ||
                        (!row.readOnly &&
                            row.won &&
                            orderStatusCount[ORDER_STATUS.WON].length > 1) ||
                        (!row.readOnly &&
                            row.lost &&
                            orderStatusCount[ORDER_STATUS.LOST].length > 1)
                        ? {
                              icon: <Icon name="trash" onClick={() => handleDelete(row)} />,
                              tooltip: { content: t('action_delete') },
                          }
                        : null;
                },
            ],
            loading: state.loading,
            useDragRows: true,
            getRowNodeId: (row) => row.id,
            onDragEnter: handleDragEnter,
            onDragEnd: handleDragEnd,
            forceActionsRefresh: true,
        };
    }, [
        t,
        getInfoForTag,
        state.data,
        state.loading,
        handleDragEnter,
        handleDragEnd,
        dispatch,
        handleEdit,
        getStatus,
        orderStatusCount,
        handleDelete,
    ]);

    const getPreComponent = useMemo(() => {
        const { adviceCount } = getHints;
        return adviceCount ? (
            <Advice type="info" showIcon>
                {adviceCount}
            </Advice>
        ) : null;
    }, [getHints]);

    const modalProps = useMemo(() => {
        let customProps = {};

        const commonProps = {
            isOpen: state.modal.isOpen,
            onRequestClose: () => dispatch({ type: types.CLOSE_MODAL }),
            onCancel: () => dispatch({ type: types.CLOSE_MODAL }),
            modalInputRef: modalInputRef,
            preComponent: getPreComponent,
        };

        switch (state.modal.type) {
            case types.MODAL_EDIT:
                customProps = {
                    title: t('labe_edit_entity', [t('label_order_satate')]),
                    onConfirm: handleEditFormSave,
                    isConfirmDisabled: formHasErrors(types.MODAL_EDIT),
                };
                break;
            case types.MODAL_ADD:
                customProps = {
                    title: t('label_orders_states_title'),
                    description: t('label_orders_states_modal_desc'),
                    onConfirm: handleAddFormSave,
                    isConfirmDisabled: formHasErrors(types.MODAL_ADD),
                };
                break;
            default:
                break;
        }

        return {
            ...commonProps,
            ...customProps,
        };
    }, [
        state.modal.isOpen,
        state.modal.type,
        getPreComponent,
        dispatch,
        t,
        handleEditFormSave,
        handleAddFormSave,
        formHasErrors,
    ]);

    const modalContent = useMemo(() => {
        let modalContentProps = null;
        let ModalContent = null;

        switch (state.modal.type) {
            case types.MODAL_EDIT:
                modalContentProps = {
                    form: state.modal[types.MODAL_EDIT],
                    onChange: handleEditFormChange,
                    onBlur: handleEditFormBlur,
                    schema: formSchema,
                    modalInputRef,
                };
                ModalContent = EditForm;
                break;
            case types.MODAL_ADD:
                modalContentProps = {
                    form: state.modal[types.MODAL_ADD],
                    onChange: handleAddFormChange,
                    onBlur: handleAddFormBlur,
                    schema: formSchema,
                    modalInputRef,
                };
                ModalContent = AddForm;
                break;
            default:
                break;
        }

        if (!ModalContent) return null;

        return <ModalContent {...modalContentProps} />;
    }, [
        state,
        handleEditFormChange,
        handleEditFormBlur,
        handleAddFormChange,
        handleAddFormBlur,
        formSchema,
    ]);

    return (
        <Fragment>
            <Table {...tableProps} />
            <Modal {...modalProps}>{modalContent}</Modal>
        </Fragment>
    );
};

export default memo(OrdersStates);
